import { Box, Pagination, PaginationItem } from "@mui/material";
import { useCallback, useEffect, useRef } from "react";

import type { PaginationRenderItemParams } from "@mui/material/Pagination";

import {
  FirstPageIcon,
  KeyboardArrowLeftIcon,
  KeyboardArrowRightIcon,
  LastPageIcon,
} from "@/icons";

export const FRAMES_PAGE_SIZE = 24;

/** Collect all scrollable ancestors from element up to document (nearest first) */
function getScrollableAncestors(element: HTMLElement): HTMLElement[] {
  const result: HTMLElement[] = [];
  let parent = element.parentElement;
  while (parent) {
    const { overflowY } = getComputedStyle(parent);
    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      parent.scrollHeight > parent.clientHeight
    ) {
      result.push(parent);
    }
    parent = parent.parentElement;
  }
  return result;
}

export interface TilesPaginationProps {
  count: number;
  page: number;
  onChange: (event: React.ChangeEvent<unknown>, newPage: number) => void;
}

export function TilesPagination({
  count,
  page,
  onChange,
}: TilesPaginationProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const prevPageRef = useRef<number | undefined>(undefined);

  const handleChange = useCallback(
    (event: React.ChangeEvent<unknown>, newPage: number) => {
      onChange(event, newPage);
    },
    [onChange],
  );

  // Scroll to top after page change and new content has rendered (instant, no animation)
  useEffect(() => {
    const prevPage = prevPageRef.current;
    prevPageRef.current = page;
    if (prevPage === undefined) return; // skip initial mount
    const el = containerRef.current;
    if (!el) return;
    const scrollables = getScrollableAncestors(el);
    const run = () => {
      for (const node of scrollables) {
        node.scrollTo({ top: 0, behavior: "auto" });
      }
    };
    requestAnimationFrame(() => requestAnimationFrame(run));
  }, [page]);

  return (
    <Box
      ref={containerRef}
      sx={{
        py: 4,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Pagination
        count={count}
        page={page}
        onChange={handleChange}
        showFirstButton
        showLastButton
        siblingCount={2}
        sx={{
          "& .MuiPaginationItem-root": {
            fontSize: "14px",
            fontWeight: 500,
            minWidth: 36,
            height: 36,
            borderRadius: "50%",
            mx: 2,
            "&:hover": {
              backgroundColor: "background.paper-elevation-3",
            },
          },
          "& .MuiPaginationItem-page.Mui-selected": {
            color: "text.primary",
            "&:hover": {
              backgroundColor: "background.paper-elevation-3",
            },
          },
          "& .MuiPaginationItem-firstLast, & .MuiPaginationItem-previousNext": {
            color: "text.primary",
          },
        }}
        renderItem={(item: PaginationRenderItemParams) => (
          <PaginationItem
            slots={{
              first: FirstPageIcon,
              last: LastPageIcon,
              previous: KeyboardArrowLeftIcon,
              next: KeyboardArrowRightIcon,
            }}
            {...item}
          />
        )}
      />
    </Box>
  );
}
